<script setup lang="ts">
/**
 * Venstre sidebar — sticky, kollapsbar per D-011/D-021/D-026 + 03-ui-design.md.
 *
 * Bredde animerer mellem w-14 (kollapset, kun ikoner) og w-56 (udvidet).
 * Persisteret state ligger i global state (sidebarCollapsed).
 * Sammenklappet folder den sig ud ved mouse-over som overlay og lukker ved
 * klik på et menupunkt; klik på sidebar-ikonet fastgør den (Charlie
 * 19-10-2026, se HOVER-UDFOLDNING).
 *
 * Tre lodrette grupper (flex-col):
 *   1. Top: burger-toggle, brand, primær navigation.
 *   2. Spacer (flex-1).
 *   3. Bund: Driftstatus med Kørsler + Log + Manuel sync under sig
 *      (Charlie 18-08-2026); Log ud som ikon ved brugernavnet
 *      ([D-155] — tema-valget bor på Indstillinger → Min profil; topbaren har
 *      desuden en hurtig-toggle Lys/Mørk ved brandet, Charlie 07-08).
 *
 * Ingen lodret skel-linje mod indholdet (Charlie 27-09-2026): sidebaren og
 * siden deler baggrund, og menuen står fri uden kant.
 */

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { auth, logout } from '../composables/useAuth';
import { canAccessPath, canEditPath, catalogPages, permissionsState } from '../composables/usePermissions';
import { BLENDED_BASE_PATH, BLENDED_PAGES, CAMPAIGN_MENU, CAMPAIGNS_BASE_PATH } from '@cockpit/shared';
import { activeCampaignMenuPath } from '../views/marketing/campaigns/campaign-nav';
import { goToSection } from '../lib/section-hash';
import { kindLabel, searchAll, type SearchHit } from '../lib/search';
import { useTheme } from '../composables/useTheme';
import type { RoleCode } from '../permissions';
import { ICONS, type IconName } from '../icons';
import { state } from '../state';
import SyncCascade from './SyncCascade.vue';
import AiDots from './AiDots.vue';
import { aiActivityForMenu, aiActivityTip, startAiActivityPolling } from '../composables/useAiActivity';

// Log ud — door-arrow-ikon i samme 1.5px-outline-stil som de øvrige.
const logoutIcon =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>';

function onLogout(): void {
  void logout();
}

/**
 * Tema-hurtigtoggle ([D-198], Charlie 17-08-2026): ÉT ikon ad gangen frem for
 * to segmenter, og flyttet ned i bund-rækken til venstre for logud-ikonet.
 *
 * Ikonet viser hvad man SKIFTER TIL, ikke hvad man har — det er den udbredte
 * konvention (som i browsere og telefoner) og den eneste der kan læses uden en
 * markeret tilstand at sammenligne med: står man i lyst tema, viser knappen en
 * måne. Tooltip og aria-label siger handlingen i ord, så der ikke gættes.
 *
 * Pladsen i topbaren gik til det globale søgefelt (samme leverance). Det fulde
 * valg bor fortsat på Indstillinger → Min profil ([D-155]); persistens deles
 * via state.theme, så de to steder altid er enige.
 */
const { theme, setTheme } = useTheme();

const themeIsDark = computed(() => theme.value === 'dark');
const themeToggleLabel = computed(() =>
  themeIsDark.value ? 'Skift til lyst tema' : 'Skift til mørkt tema',
);
function toggleTheme(): void {
  setTheme(themeIsDark.value ? 'light' : 'dark');
}

/** Indlogget brugers visningsnavn (fallback til delen før @ i emailen). */
const userName = computed(() => {
  const u = auth.user;
  if (!u) return '';
  return u.displayName?.trim() || u.email.split('@')[0];
});

/**
 * Initialer til avatar-fallback. Ingen profilbillede er gemt (ville kræve
 * Microsoft Graph ved login), så vi viser initialer som Teams/Outlook gør.
 * Tager op til 2 bogstaver fra navnedelen før en evt. parentes (firma).
 */
const userInitials = computed(() => {
  const u = auth.user;
  if (!u) return '';
  const base = (u.displayName?.trim() || u.email).split('(')[0]!.trim();
  const words = base.split(/\s+/).filter((w) => /^\p{L}/u.test(w));
  const ini = words.slice(0, 2).map((w) => w[0] ?? '').join('') || u.email[0] || '?';
  return ini.toUpperCase();
});

/**
 * Viewport-bredde under hvilken sidebar tvangs-kollapses uanset brugerens
 * manuelle valg. 900 px valgt for at give plads til sidebar (56 px kollapset
 * eller 224 px udvidet) + sticky filter-header + meningsfuldt indhold-areal.
 * Under det skifter vi til kun-ikoner for at give indholdet mere plads.
 */
const SIDEBAR_AUTO_COLLAPSE_PX = 900;

interface ThirdLevelItem {
  path: string;
  label: string;
}

interface SubNavItem {
  path: string;
  label: string;
  /** Niveau 3 ([D-150]/[D-321]): undersider vist INDE i den markerede niveau
   *  2-boks når gruppen er aktiv (før 26-09-2026 i en ekstra kolonne, der åd
   *  sidebredde). Gruppens eget path er kun et prefix (ingen egen side) —
   *  klik navigerer til første synlige barn. */
  children?: ThirdLevelItem[];
}

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  /** Undermenuer vist indrykket under hovedmenuen når sektionen er aktiv. */
  subItems?: SubNavItem[];
  /**
   * DYNAMISKE undermenuer ([D-215]): sider hvis stier ikke kendes i koden,
   * fordi de kommer af data — hver vækstcase er sin egen katalog-side. Punkterne
   * bygges af side-KATALOGET (titel + sti fra /api/settings/permissions) og
   * filtreres på grants som alle andre. Hovedmenuens egen sti indgår ØVERST,
   * hvis brugeren må se den.
   */
  dynamicPrefix?: string;
}

const navItems: NavItem[] = [
  {
    path: '/marketing',
    label: 'Marketing',
    icon: 'megaphone',
    // [D-183]: Performance delt op i én tværgående side + én pr. kanal.
    // [D-198]: den tværgående side heder nu Blended og bor på egen slug.
    subItems: [
      // Blended ([D-556]): menugruppe på 3. niveau med Månedstal ([D-335]) som
      // første underside; listen bor i shared (BLENDED_PAGES).
      {
        path: BLENDED_BASE_PATH,
        label: 'Blended',
        children: BLENDED_PAGES.map((p) => ({ path: p.path, label: p.label })),
      },
      { path: '/marketing/paid-search', label: 'Paid search' },
      { path: '/marketing/paid-social', label: 'Paid social' },
      { path: '/marketing/organic-search', label: 'Organic search' },
      // Konkurrenter ([D-292]): konkurrentlisten m. regnskab, trafik og agent.
      { path: '/marketing/competitors', label: 'Konkurrenter' },
      // Organic SoMe ([D-383]): menugruppe på 3. niveau. Kanal-siden lever
      // videre på sin slug som barnet Oversigt (fortsat grå, meta.empty);
      // Overvågning er gruppens første rigtige underside.
      {
        path: '/marketing/organic-social',
        label: 'Organic SoMe',
        children: [
          { path: '/marketing/organic-social', label: 'Oversigt' },
          { path: '/marketing/organic-social/monitoring', label: 'Overvågning' },
        ],
      },
      { path: '/marketing/email', label: 'Email' },
      { path: '/marketing/sms', label: 'SMS' },
      { path: '/marketing/print', label: 'Print' },
      { path: '/marketing/outdoor', label: 'Outdoor' },
      { path: '/marketing/spend-budget', label: 'Spend-budget' },
      // Kampagner ([D-304]): 5 undermenuer efter kampagnens fase. Navigation,
      // ikke adgang: stierne har ingen egne katalog-sider og arver grantet på
      // '/marketing/campaigns'.
      {
        path: CAMPAIGNS_BASE_PATH,
        label: 'Kampagner',
        // [D-420]: Dashboards står efter faserne (monitor-visningen pr. kampagne).
        children: [
          ...CAMPAIGN_MENU.map((m) => ({ path: `${CAMPAIGNS_BASE_PATH}/${m.slug}`, label: m.label })),
          { path: `${CAMPAIGNS_BASE_PATH}/dashboards`, label: 'Dashboards' },
        ],
      },
      { path: '/marketing/retainers', label: 'Retainere' },
      { path: '/marketing/qr-codes', label: 'QR-koder' },
      // Index ([D-252]): marketing-nøgletallene måned for måned på tværs af
      // driftårene — søstersiden til Regnskab → Index.
      { path: '/marketing/index', label: 'Index' },
      // Customer Match ([D-285]): kundelister + offline-konverteringer fra BC.
      { path: '/marketing/customer-match', label: 'Customer Match' },
      // Affiliates ([D-349]): én underside pr. partner (3. niveau).
      {
        path: '/marketing/affiliates',
        label: 'Affiliates',
        children: [{ path: '/marketing/affiliates/loyalty-key', label: 'Loyalty Key' }],
      },
      // Brand guidelines ([D-425]): CVI'en som undersider (3. niveau).
      {
        path: '/marketing/brand',
        label: 'Brand guidelines',
        children: [
          { path: '/marketing/brand/overview', label: 'Overblik' },
          { path: '/marketing/brand/visual-identity', label: 'Visuel identitet' },
          { path: '/marketing/brand/tone-of-voice', label: 'Tone of voice' },
          { path: '/marketing/brand/core-story', label: 'Kernefortælling' },
          { path: '/marketing/brand/code-of-conduct', label: 'Code of conduct' },
        ],
      },
    ],
  },
  // Analyser ([D-215], 19-08-2026): EGEN hovedmenu. Undermenuen bygges af
  // side-kataloget (dynamicPrefix nedenfor), fordi hver analyse er sin egen
  // side med sit eget grant — sidebaren skal derfor vise præcis de analyser
  // brugeren må se, og en ny analyse skal dukke op af sig selv.
  // [D-289] (11-09-2026): undermenuen viser KATEGORIERNE (ANALYSIS_CATEGORIES
  // i shared/analyses.ts, fx Samarbejder,
  // Konsulenter, SaaS, Performance, Finans) som niveau 2 og analyserne som
  // niveau 3 (kolonnen der glider ind). Kategorien kommer fra kataloget
  // (`group`); en kategori uden synlige analyser findes ikke for brugeren.
  { path: '/analyses', label: 'Analyser', icon: 'trendingUp', dynamicPrefix: '/analyses/' },
  {
    path: '/inbound',
    label: 'Inbound',
    icon: 'phoneIncoming',
    subItems: [
      { path: '/inbound/phone', label: 'Telefon' },
      { path: '/inbound/store-visits', label: 'Butiksbesøg' },
      { path: '/inbound/inquiries', label: 'Henvendelser' },
      { path: '/inbound/bookings', label: 'Bookinger' },
      { path: '/inbound/leads', label: 'Leads' },
      { path: '/inbound/competitions', label: 'Konkurrencer' },
    ],
  },
  // Webshop ([D-226], 24-08-2026): SEO-motoren som niveau 3-gruppe (samme
  // mekanik som [D-150]) — webshoppen kommer til at rumme flere funktioner
  // end SEO, så SEO-siderne holdes i deres eget spor fra starten.
  {
    path: '/webshop',
    label: 'Webshop',
    icon: 'shoppingCart',
    subItems: [
      {
        path: '/webshop/seo',
        label: 'SEO',
        children: [
          { path: '/webshop/seo/review', label: 'Til gennemgang' },
          { path: '/webshop/seo/products', label: 'Produkter' },
          { path: '/webshop/seo/categories', label: 'Kategorier' },
          { path: '/webshop/seo/pages', label: 'Sider' },
          { path: '/webshop/seo/keywords', label: 'Søgeord' },
          // Regelsæt ([D-233]): det levende regelsæt (kladde → prøve-render → aktivér).
          { path: '/webshop/seo/rules', label: 'Regelsæt' },
          // Menuer ([D-242]): sitets navigation m. menu-tjekket (kun læsning).
          { path: '/webshop/seo/menus', label: 'Menuer' },
          // Redirects ([D-319]): omdirigering/nedlæggelse m. auto-oprydning af links.
          { path: '/webshop/seo/redirects', label: 'Redirects' },
          // Log ([D-320]): publicerede indholdsændringer + målt effekt.
          { path: '/webshop/seo/log', label: 'Log' },
        ],
      },
      // CRO ([D-241]): konverterings-sporet — 2. niveau 3-gruppe under Webshop.
      {
        path: '/webshop/cro',
        label: 'CRO',
        children: [
          { path: '/webshop/cro/conversion', label: 'Konvertering' },
          { path: '/webshop/cro/pagespeed', label: 'Pagespeed' },
        ],
      },
      // Billeder ([D-327]): AI-opgraderede produktbilleder til godkendelse —
      // 3. niveau 3-gruppe. Kategorier/Sider er grå (meta.empty) til de bygges.
      {
        path: '/webshop/images',
        label: 'Billeder',
        children: [
          { path: '/webshop/images/review', label: 'Til gennemgang' },
          { path: '/webshop/images/products', label: 'Produkter' },
          { path: '/webshop/images/categories', label: 'Kategorier' },
          { path: '/webshop/images/pages', label: 'Sider' },
          { path: '/webshop/images/rules', label: 'Regelsæt' },
          // Validering ([D-327] v1.3): afvisnings-årsager + klassificering.
          { path: '/webshop/images/validation', label: 'Validering' },
        ],
      },
      // Relewise ([D-364]): shoppens søge- og anbefalingsmotor.
      // Index ([D-553]): Relewise-vagten mellem WP og Relewise. Gruppe på 3. niveau
      // som Organic SoMe; oversigten lever videre på sin slug.
      {
        path: '/webshop/relewise',
        label: 'Relewise',
        children: [
          { path: '/webshop/relewise', label: 'Oversigt' },
          { path: '/webshop/relewise/index', label: 'Index' },
        ],
      },
      // Feeds ([D-373]): produktfeeds til annonce-kanaler (Meta/Google).
      { path: '/webshop/feeds', label: 'Feeds' },
    ],
  },
  {
    path: '/stores',
    label: 'Butikker',
    icon: 'store',
    subItems: [
      { path: '/stores', label: 'Åbningstider' },
      { path: '/stores/monitors', label: 'Infoskærme' },
      { path: '/stores/salesstats', label: 'Salgsstatistik' },
      { path: '/stores/pipeline', label: 'Pipeline' },
      // Prisskilte ([D-343]): digitale prisskilte (Breece Cloud), 3. niveau.
      {
        path: '/stores/price-tags',
        label: 'Prisskilte',
        children: [
          { path: '/stores/price-tags/signs', label: 'Skilte' },
          { path: '/stores/price-tags/rules', label: 'Regelsæt' },
          { path: '/stores/price-tags/showrooms', label: 'Udstillingsmiljøer' },
        ],
      },
    ],
  },
  {
    path: '/customers',
    label: 'Kunder',
    icon: 'users',
    subItems: [
      { path: '/customers', label: 'Kunder' },
      // Rabatgrupper ([D-169]-udvidelsen): rabatgruppe-vagtens kandidater.
      { path: '/customers/discount-groups', label: 'Rabatgrupper' },
      // Kunderejsen ([D-417]): touchpoints, indgange, genkøb og CLV.
      { path: '/customers/journey', label: 'Kunderejsen' },
    ],
  },
  // Varer ([D-222], 20-08-2026): vare-siderne lå under Budget, men handler om
  // husets sortiment, ikke om budgettet. Egen hovedmenu.
  {
    path: '/items',
    label: 'Varer',
    icon: 'packageCheck',
    subItems: [
      { path: '/items/prices', label: 'Varepriser' },
      { path: '/items/discounts', label: 'Rabatstyring' },
      // Leverandører (feat/leverandorer-side): kontrolpanel til AI-agenterne
      // (Kostpris + Salgspris). Kobler BC-leverandører til SharePoint-mapper.
      { path: '/items/vendors', label: 'Leverandører' },
      // Log: kørsler foretaget af Salgspris-agenten (system_log grupperet).
      { path: '/items/log', label: 'Log' },
      // Prishistorik: søg vare frem, se tidslinje af alle prisændringer.
      { path: '/items/price-history', label: 'Prishistorik' },
      // Vareopslag ([D-498]): hele rapporten for 1 vare + gennemtving BC til WP.
      { path: '/items/lookup', label: 'Vareopslag' },
      // Lagervarer ([D-300]; før Lagerudsalg på /items/clearance, [D-291]):
      // alle lagervarer ifølge BC m. valgfri periode uden salg.
      { path: '/items/inventory', label: 'Lagervarer' },
    ],
  },
  // Logistik ([D-222]): egen hovedmenu til fragt.
  {
    path: '/logistics',
    label: 'Logistik',
    icon: 'truck',
    subItems: [{ path: '/logistics/shipping-prices', label: 'Fragtpriser' }],
  },
  // Regnskab ([D-222]): hed Budget indtil 20-08-2026. Med vare- og fragtsiderne
  // ude er sektionen ren økonomi, og siden med driftbudgettet heder derfor
  // simpelthen Budget (Oversigt indtil [D-199], Driftbudget indtil [D-222]).
  {
    path: '/accounting',
    label: 'Regnskab',
    icon: 'wallet',
    subItems: [
      { path: '/accounting/budget', label: 'Budget' },
      // Periodeafstemning ([D-184]): listen bag vagt-banneret på Budget-siden.
      { path: '/accounting/reconciliation', label: 'Periodeafstemning' },
      { path: '/accounting/accounts', label: 'Konti & sektioner' },
      // Udgiftsbilag ([D-391]): bilags-mails → bogholderiet, godkendelseskøen.
      { path: '/accounting/expense-docs', label: 'Udgiftsbilag' },
      // Index ([D-251]): nøgletallene måned for måned på tværs af driftårene.
      // [D-306] (25-09-2026): niveau 3-gruppe med Finansielt (den hidtidige
      // side), Marked (leverandørernes markedsindex mod husets) og Vækst
      // (omsætningsvækst mod vækst i annonceforbruget). Gruppens egen sti
      // redirecter til Finansielt; hvert barn er sin egen katalog-side.
      {
        path: '/accounting/index',
        label: 'Index',
        children: [
          { path: '/accounting/index/financial', label: 'Finansielt' },
          { path: '/accounting/index/market', label: 'Marked' },
          { path: '/accounting/index/growth', label: 'Vækst' },
          // Leverandører ([D-306] v1.2): varesalg pr. leverandør, index år-til-år.
          { path: '/accounting/index/suppliers', label: 'Leverandører' },
        ],
      },
    ],
  },
  // Strategi ([D-223]): Diagnose flyttet fra Marketing til egen hovedmenu —
  // diagnosen dækker hele forretningen, ikke kun marketing.
  {
    path: '/strategy',
    label: 'Strategi',
    // Eget ikon (20-08-2026): 'activity' bruges af Driftstatus i bund-gruppen.
    icon: 'chessRook',
    subItems: [
      { path: '/strategy/diagnosis', label: 'Diagnose' },
      { path: '/strategy/presentations', label: 'Præsentationer' },
    ],
  },
  {
    path: '/doc',
    label: 'Dokumentation',
    icon: 'book',
    subItems: [
      // Rækkefølge = Charlies bestilling 13-08-2026 (fag-siderne først, derefter
      // opslagsværker, sikkerhed og agenter) — spejles i katalogets sortOrder.
      { path: '/doc/phone', label: 'Telefon' },
      { path: '/doc/store-visits', label: 'Butiksbesøg' },
      { path: '/doc/attribution/revenue', label: 'Omsætning' },
      { path: '/doc/gross-profit', label: 'Bruttofortj. og avancer' },
      { path: '/doc/budget', label: 'Budget' },
      { path: '/doc/glossary', label: 'Ordbog' },
      { path: '/doc/freshness', label: 'Datafriskhed' },
      { path: '/doc/security', label: 'Sikkerhedspolitik' },
      { path: '/doc/agents', label: 'Agenter' },
      // SEO ([D-226]): teknik + regelsæt + brand-lag for SEO-motoren.
      { path: '/doc/seo', label: 'SEO' },
      { path: '/doc/customer-match', label: 'Customer Match' },
    ],
  },
  {
    path: '/settings',
    label: 'Indstillinger',
    icon: 'settings',
    subItems: [
      // Min profil ([D-155]) er BEVIDST uden katalog-side: alle loggede ind
      // skal kunne nå deres egen profil (tema-valg) — canAccessPath er åben
      // for ruter uden for kataloget, så punktet vises for alle.
      { path: '/settings/profile', label: 'Min profil' },
      // [D-141]: Rettigheder-siden er udgået — grants redigeres pr. bruger
      // på /settings/users/:id (klik på en række under Brugere).
      { path: '/settings/users', label: 'Brugere' },
      // Siderettigheder ([D-178]): side → brugere-vinklen på grants +
      // skabelon-redigering; rolle-gated som Brugere.
      { path: '/settings/page-permissions', label: 'Siderettigheder' },
      { path: '/settings/api-keys', label: 'API-nøgler' },
      // Sprogmodeller ([D-234]): den rette model til opgaven, skift logges.
      { path: '/settings/llm', label: 'Sprogmodeller' },
      // Driftstatus OG Systemlog er FLYTTET ud af denne liste (Charlie
      // 18-08-2026) og bor nu samlet i bund-gruppen som Driftstatus →
      // Systemlog + Sync data. Se kommentaren ved bund-gruppen i template'n.
      // Notifikationer ([D-169]): hvem modtager Cockpits automatiske mails.
      { path: '/settings/notifications', label: 'Notifikationer' },
      // Sideindstillinger (rev 060): samlet oversigt over siders tandhjul.
      { path: '/settings/page-settings', label: 'Sideindstillinger' },
    ],
  },
];

/**
 * Sync-cascade state — åbnes når Charlie klikker "Sync data"-knappen i
 * bund-gruppen. Cascade'n er en centreret modal (undtagelse til [§14] for
 * admin-meta-actions per [Fase A11.1]); den er IKKE en sub-route. Det
 * manuelle modstykke til fremtidigt cron-modul.
 */
const syncCascadeOpen = ref(false);

const route = useRoute();

/**
 * Alle sektioner der KAN være åbne: hovedmenuens punkter + Driftstatus-gruppen
 * i bunden, som opfører sig som en sektion selv om den ikke står i navItems.
 */
const SECTION_PATHS: string[] = [...navItems.map((i) => i.path), '/ops'];

/** Matcher ruten stien som et helt sti-led? '/settings' må ikke ramme
 *  '/settingsfoo', og '/marketing' skal ramme '/marketing/blended/kpi'. */
function pathMatches(prefix: string): boolean {
  return route.path === prefix || route.path.startsWith(`${prefix}/`);
}

/**
 * KUN ÉN sektion er åben ad gangen (Charlie 18-08-2026: *"når man åbner
 * Driftstatus-menuen, skal andre evt. åbne menuer jo lukke"*) — den MEST
 * SPECIFIKKE sektion der matcher ruten.
 *
 * ⚠ Uden dette var to menuer åbne samtidig, og det var ikke et skønhedsvalg
 * men en direkte følge af prefix-matchet: `/settings/ops` startede med
 * `/settings`, så Indstillinger regnede sig selv for aktiv og foldede sine syv
 * punkter ud under Driftstatus-gruppen. Samme fælde som aktiv-markeringen i
 * undermenuen ([D-183]) — et prefix er ikke det samme som en sektion.
 *
 * Netop dét eksempel er væk med [D-222] (Driftstatus bor nu på '/ops'), men
 * reglen bliver stående: den gælder hver gang en sektions-sti er prefix af en
 * anden, og det sker igen næste gang nogen lægger en side under et fælles led.
 */
const activeSectionPath = computed<string | null>(() => {
  const hits = SECTION_PATHS.filter(pathMatches);
  if (hits.length === 0) return null;
  return hits.reduce((longest, p) => (p.length > longest.length ? p : longest));
});

/**
 * En sektion er "aktiv" hvis den ER den aktive sektion — ikke blot et prefix af
 * ruten. Bruges til at vise undermenuer kun for den ene åbne sektion.
 */
function isSectionActive(path: string): boolean {
  return activeSectionPath.value === path;
}

/**
 * Hvor hovedmenuens link skal navigere til. For sektioner med undermenuer
 * peger linket direkte på den første undermenu (fx /settings/api-keys), så
 * klik altid lander på et konkret indhold — uden at lave en runtime-redirect
 * gennem parent-routen. Sektioner uden undermenuer bruger deres egen path.
 */
function resolveMainPath(item: NavItem): string {
  const firstSub = item.subItems?.[0];
  if (firstSub) {
    // resolveSubPath, ikke firstSub.path: er første punkt en GRUPPE (fx
    // Webshop → SEO), er dens egen sti kun et prefix/en redirect — linket
    // skal pege på det første synlige BARN, så permission-guarden aldrig
    // afviser en sti brugeren reelt har adgang bag (26-09-2026).
    return resolveSubPath(firstSub);
  }
  return item.path;
}

/** Driftstatus-genvejen ([D-155]) — grant-gated som nav-punkterne. */
const canSeeOps = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return canAccessPath('/ops/jobs', roles);
});

/**
 * Systemloggen ([D-202]) har sit EGET grant, netop så en ekstern konsulent kan
 * få loggen uden resten af Driftstatus. Derfor er den gated for sig — og derfor
 * kan punktet stå i bund-gruppen UDEN sin overskrift: har man kun loggen, er
 * "Driftstatus" en side man ikke må se, og en overskrift der linker til en 403
 * er værre end ingen overskrift.
 */
const canSeeSystemLog = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return canAccessPath('/ops/log', roles);
});

/** Platformvagtens fund ([D-402]) — eget grant som de øvrige Driftstatus-sider. */
const canSeeOpsActions = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return canAccessPath('/ops/actions', roles);
});

/** Driftsforstyrrelser ([D-418]) — eget grant som de øvrige Driftstatus-sider. */
const canSeeOpsDisruptions = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return canAccessPath('/ops/disruptions', roles);
});

/**
 * Driftstatus-gruppen i bunden (Charlie 18-08-2026): overskriften findes kun
 * hvis mindst ét af de tre underpunkter må ses. Ellers ville en tom gruppe stå
 * tilbage og love noget der ikke er der.
 */
const opsGroupVisible = computed(
  () => canSeeOps.value || canSeeSystemLog.value || canSeeOpsActions.value || canSeeOpsDisruptions.value || canSyncData.value,
);

/**
 * Hvor overskriften navigerer hen: FØRSTE synlige underpunkt, præcis som
 * hovedmenuens sektioner (resolveMainPath). Har man kun Systemlog, lander
 * klikket på Systemlog — ikke på en Driftstatus-side man får 403 på. Er ingen
 * af de to SIDER synlige (kun Sync data), er der intet at navigere til, og
 * overskriften bliver en ren etiket.
 */
/**
 * Er gruppen ÅBEN? (Charlie 18-08-2026: *"undermenuer til Driftstatus skal kun
 * vises, når man åbner hovedmenuen Driftstatus"*.)
 *
 * Samme regel som hovedmenuens sektioner bruger: undermenuen hører til den
 * aktive sektion, ikke til skærmen. Uden den stod Driftstatus' tre punkter
 * permanent i bunden og gjorde menuen tungere, uanset hvor man var.
 *
 * Prefix-baseret, så både `/ops/jobs` og `/ops/log` holder gruppen åben — man
 * skal ikke miste undermenuen ved at klikke på et af dens punkter.
 */
const opsGroupOpen = computed(() => isSectionActive('/ops'));

const opsGroupTarget = computed<string | null>(() => {
  if (canSeeOps.value) return '/ops/jobs';
  if (canSeeSystemLog.value) return '/ops/log';
  if (canSeeOpsActions.value) return '/ops/actions';
  if (canSeeOpsDisruptions.value) return '/ops/disruptions';
  return null;
});

/** Filtrerede nav-items baseret på brugerens roller. Grupper m. niveau 3
 *  ([D-150]) filtrerer BØRNENE på grants og skjules helt uden synlige børn. */
const visibleNavItems = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return navItems.flatMap((item) => {
    // Dynamiske sektioner ([D-215]): undermenuen ER katalogets sider under
    // prefixet, sorteret som kataloget. Har man kun ÉN case og ikke oversigten,
    // står casen alene under overskriften — og oversigten optræder ikke, fordi
    // canAccessPath er fail-closed på den.
    if (item.dynamicPrefix) {
      const subs: SubNavItem[] = [];
      if (canAccessPath(item.path, roles)) subs.push({ path: item.path, label: 'Alle analyser' });
      // [D-289]: siderne grupperes på katalogets `group` (analysens kategori)
      // i katalogets orden (kategori-orden, så registrets). Gruppens egen sti
      // er SYNTETISK — den findes ikke som rute; klik lander på første barn
      // (resolveSubPath), og aktiv-tilstanden afgøres af børnene
      // (groupIsActive), fordi analyserne bor på /analyses/<uid> uden
      // kategori i stien. Sider uden group (kun i fallback-kataloget, hvor
      // titlen er stien) samles under "Øvrige".
      const groups = new Map<string, ThirdLevelItem[]>();
      for (const page of [...catalogPages().pages].sort((a, b) => a.sortOrder - b.sortOrder)) {
        if (!page.path.startsWith(item.dynamicPrefix)) continue;
        if (!canAccessPath(page.path, roles)) continue;
        const label = page.group ?? 'Øvrige';
        const list = groups.get(label) ?? [];
        list.push({ path: page.path, label: page.title });
        groups.set(label, list);
      }
      for (const [label, children] of groups) {
        subs.push({ path: `${item.path}/kategori/${encodeURIComponent(label.toLowerCase())}`, label, children });
      }
      return subs.length > 0 ? [{ ...item, subItems: subs }] : [];
    }
    if (item.subItems) {
      const visibleSubs = item.subItems.flatMap((s) => {
        if (s.children) {
          const visibleChildren = s.children.filter((c) => canAccessPath(c.path, roles));
          return visibleChildren.length > 0 ? [{ ...s, children: visibleChildren }] : [];
        }
        return canAccessPath(s.path, roles) ? [s] : [];
      });
      if (visibleSubs.length === 0) return [];
      return [{ ...item, subItems: visibleSubs }];
    }
    return canAccessPath(item.path, roles) ? [item] : [];
  });
});

/** Undermenu-linkets mål: grupper lander deterministisk på første synlige barn. */
function resolveSubPath(sub: SubNavItem): string {
  return sub.children?.[0]?.path ?? sub.path;
}

/**
 * Er en niveau 3-gruppe aktiv? Enten ligger ruten under gruppens prefix
 * (Webshop → SEO → /webshop/seo/…), eller ruten ER et af gruppens børn
 * ([D-289]: analysernes kategorier har børn på /analyses/<uid>, som ikke
 * deler prefix med den syntetiske gruppe-sti). Uden det andet ben ville
 * kolonnen aldrig åbne for en analyse, og kategorien aldrig blive markeret.
 */
function groupIsActive(sub: SubNavItem): boolean {
  if (!sub.children) return false;
  if (route.path === sub.path || route.path.startsWith(`${sub.path}/`)) return true;
  return sub.children.some((c) => c.path === route.path);
}

/**
 * Sync data er en admin-meta-action (usePermission er rute-bundet og kan ikke
 * bruges her); gates via canEditPath på /settings/api-keys ligesom nav-filtret.
 */
const canSyncData = computed(() => {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  return canEditPath('/settings/api-keys', roles);
});

/**
 * Track viewport-bredde med en resize-listener. Når vinduet er smallere end
 * SIDEBAR_AUTO_COLLAPSE_PX, force-collapser vi uanset brugerens valg.
 * Burger-knappen forbliver klikbar, men toggle har ingen synlig effekt før
 * brugeren gør vinduet bredere.
 */
const viewportTooNarrow = ref(false);

function checkViewport(): void {
  viewportTooNarrow.value = window.innerWidth < SIDEBAR_AUTO_COLLAPSE_PX;
  // Drejes telefonen, eller gøres vinduet bredt, forsvinder baren (md:hidden),
  // og et åbent ark må ikke blive hængende usynligt med låst scroll.
  if (window.innerWidth >= MOBILE_NAV_MAX_PX && mobileSheet.value !== null) closeMobileSheet();
}

onMounted(() => {
  checkViewport();
  window.addEventListener('resize', checkViewport);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewport);
});

/** Er menuen klappet sammen i LAYOUTET (brugerens valg eller for smalt vindue)?
 *  Afgør hvor meget plads sidebaren tager fra indholdet. */
const pinnedCollapsed = computed(() => state.sidebarCollapsed || viewportTooNarrow.value);

/** Midlertidigt foldet ud ved mouse-over (se HOVER-UDFOLDNING længere nede). */
const hoverOpen = ref(false);

/** Tegnes menuen sammenklappet (kun ikoner)? Den VISUELLE tilstand: en
 *  hover-udfoldet menu tegnes fuldt ud, men tager stadig kun 56 px af layoutet. */
const collapsed = computed(() => pinnedCollapsed.value && !hoverOpen.value);

// ---------------------------------------------------------------------------
// MOBILENS FOOTER-MENU (Charlie 25-09-2026)
// ---------------------------------------------------------------------------
//
// "Menuen bør ikke fylde hele venstre side af vinduet på mobilen." Under 768 px
// (Tailwinds md) er sidebaren skjult, og en app-lignende bar står fast i
// bunden: ét ikon pr. hovedmenu, vandret scrollbar, så man slider mellem dem.
// Et tryk på en sektion MED undermenu åbner et ark nedefra, der fylder skærmen
// over baren med undermenuen centreret; en sektion uden undermenu navigerer
// direkte. Arket lukker ved navigation, ved tryk på samme ikon og ved krydset.
//
// Baren bor HER og ikke i en egen komponent, fordi den er en 2. tegning af
// SAMME menu: grants (visibleNavItems), den aktive sektion, niveau 3 og
// Driftstatus-gruppen afgøres ét sted, så de 2 menuer ikke kan drifte.

/** Hvad arket viser: en hovedmenus sti, Driftstatus, søgningen eller profilen. */
type MobileSheet = string | 'ops' | 'search' | 'profile';

const MOBILE_NAV_MAX_PX = 768;
const mobileSheet = ref<MobileSheet | null>(null);
const mobileNavScroller = ref<HTMLElement | null>(null);

const mobileSheetItem = computed<NavItem | null>(
  () => visibleNavItems.value.find((i) => i.path === mobileSheet.value) ?? null,
);

/**
 * NIVEAU 3 I ARKET (Charlie 25-09-2026): har en hovedmenu både niveau 2 og 3,
 * viser arket først kun niveau 2, hvor en gruppe er en knap med ›. Et tryk på
 * gruppen skjuler de øvrige niveau 2-punkter, gruppen bliver overskrift med
 * tilbage-pil, og kun dens sider står under. Tilstanden nulstilles hver gang
 * arket skifter eller lukker, så arket altid åbner på oversigten.
 */
const mobileSheetGroup = ref<string | null>(null);
const mobileSheetGroupItem = computed<SubNavItem | null>(
  () => mobileSheetItem.value?.subItems?.find((s) => s.children && s.path === mobileSheetGroup.value) ?? null,
);
watch(mobileSheet, () => {
  mobileSheetGroup.value = null;
});

const mobileSheetTitle = computed(() => {
  if (mobileSheet.value === 'ops') return 'Driftstatus';
  if (mobileSheet.value === 'search') return 'Søg';
  if (mobileSheet.value === 'profile') return userName.value;
  return mobileSheetItem.value?.label ?? '';
});

const mobileSheetIcon = computed<string>(() => {
  if (mobileSheet.value === 'ops') return ICONS.activity;
  if (mobileSheet.value === 'search') return ICONS.search;
  if (mobileSheet.value === 'profile') return ICONS.users;
  const item = mobileSheetItem.value;
  return item ? ICONS[item.icon] : '';
});

function closeMobileSheet(): void {
  if (mobileSheet.value === 'search') closeSearch();
  mobileSheet.value = null;
}

/** Tryk på et ikon i baren. Samme ikon igen lukker arket (som en fane). */
function onMobileNavItem(item: NavItem): void {
  if (!item.subItems || item.subItems.length === 0) {
    closeMobileSheet();
    void router.push(resolveMainPath(item));
    return;
  }
  mobileSheet.value = mobileSheet.value === item.path ? null : item.path;
}

async function toggleMobileSheet(sheet: 'ops' | 'search' | 'profile'): Promise<void> {
  if (mobileSheet.value === sheet) {
    closeMobileSheet();
    return;
  }
  if (mobileSheet.value === 'search') closeSearch();
  mobileSheet.value = sheet;
  if (sheet === 'search') {
    // IKKE openSearch(): den folder desktop-sidebaren ud (state.sidebarCollapsed
    // er persisteret), og det skal et tryk på mobilen ikke ændre.
    searchOpen.value = true;
    await nextTick();
    searchInput.value?.focus();
  }
}

/** Ikonet står markeret for den sektion man er i, eller hvis arket er åbent. */
function mobileItemActive(key: string): boolean {
  return mobileSheet.value !== null ? mobileSheet.value === key : isSectionActive(key);
}

/** Den aktive sektions ikon rulles ind i baren, så man kan se hvor man er. */
function scrollActiveMobileItemIntoView(): void {
  const scroller = mobileNavScroller.value;
  const el = scroller?.querySelector<HTMLElement>('[data-mnav-current="true"]');
  if (!scroller || !el) return;
  const target = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
  scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
}

watch(
  () => route.fullPath,
  () => {
    closeMobileSheet();
    void nextTick(scrollActiveMobileItemIntoView);
  },
);

// Siden bag et åbent ark må ikke rulle med, når man scroller i arket.
watch(mobileSheet, (open) => {
  document.documentElement.style.overflow = open === null ? '' : 'hidden';
});

onMounted(() => void nextTick(scrollActiveMobileItemIntoView));
onUnmounted(() => {
  document.documentElement.style.overflow = '';
});

// ---------------------------------------------------------------------------
// GLOBAL SØGNING ([D-198], Charlies bestilling 17-08-2026)
// ---------------------------------------------------------------------------
//
// "Faren er nu, at man ikke kan finde vigtige oplysninger, hvis ikke man ved
// eksakt hvor man skal kigge." Søgningen bor i sidebaren, hvor tema-togglen lå:
// klik på lupen udvider menu-draweren, menupunkterne træder til side, og
// resultaterne listes i deres plads. Klik på et resultat går til siden og
// scroller blødt ned til afsnittet.
//
// Hvorfor i sidebaren og ikke som overlay: menuen ER stedet man leder efter en
// side, og et overlay ville lægge sig over det indhold man søger i. Samtidig
// slipper vi for en ny genvej-konvention — lupen står, hvor øjet allerede er.

const router = useRouter();

/**
 * SIDER UDEN INDHOLD TEGNES SVAGT (Charlie 19-08-2026): et menupunkt der fører
 * til en side der endnu ikke er bygget, står i en lysere grå end de øvrige, så
 * man kan se på menuen hvad der findes indhold på.
 *
 * Sandheden er `meta.empty` på RUTEN (se router.ts), ikke en liste her — ellers
 * ville de 2 steder drifte fra hinanden, første gang en side blev bygget ud.
 */
function isEmptyPath(path: string): boolean {
  return router.resolve(path).meta.empty === true;
}
/**
 * En GRUPPE er tom, når alt man kan nå gennem den er tomt. Derfor forbliver
 * "Kunder" almindelig (Rabatgrupper er bygget), mens "Webshop", der ikke har
 * undermenuer og selv er tom, tegnes svagt.
 *
 * ⚠ Der måles på de SYNLIGE punkter: en bruger uden grant på Rabatgrupper ser
 * kun den tomme side under Kunder, og for hende er gruppen reelt tom.
 */
function isEmptyItem(item: NavItem): boolean {
  if (item.subItems && item.subItems.length > 0) {
    return item.subItems.every((s) => isEmptyPath(resolveSubPath(s)));
  }
  return isEmptyPath(item.path);
}
/**
 * Skal DETTE undermenupunkt stå svagt? Kun når det både er tomt og ikke er den
 * side man står på.
 *
 * ⚠ Undtagelsen for den aktive rute er ikke pynt: RouterLink lægger sin
 * `active-class` (text-ink) på samme element, og 2 Tailwind-farveklasser med
 * samme specificitet afgøres af rækkefølgen i den byggede CSS, ikke af den
 * rækkefølge de skrives her. Ved at holde text-faint helt væk fra det aktive
 * punkt er der ingen konflikt at tabe.
 * [D-326]: samme fælde ramte text-muted, som står EFTER text-ink i CSS'en, så
 * alle aktive punkter havde markerings-baggrund men grå tekst. Alle aktive
 * klasser i menuen bruger derfor `!text-ink`.
 */
function subIsFaint(item: NavItem, sub: SubNavItem): boolean {
  if (!isEmptyPath(resolveSubPath(sub))) return false;
  const active = sub.children
    ? groupIsActive(sub)
    : sub.path === item.path
      ? route.path === sub.path
      : route.path === sub.path || route.path.startsWith(`${sub.path}/`);
  return !active;
}

const searchOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);
/** Tastatur-markering i resultatlisten (pil op/ned + Enter). */
const searchActive = ref(0);

const searchResults = computed<SearchHit[]>(() => {
  if (!searchOpen.value) return [];
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  const { pages } = catalogPages();
  return searchAll(searchQuery.value, pages, roles);
});

/** Er der søgt (mindst to tegn), men uden træf? */
const searchEmpty = computed(
  () => searchQuery.value.trim().length >= 2 && searchResults.value.length === 0,
);

async function openSearch(): Promise<void> {
  searchOpen.value = true;
  // Søgning kræver den udvidede drawer — er sidebaren kollapset, foldes den ud,
  // ellers ville feltet være usynligt i en 56 px kolonne. Er den kun
  // hover-udfoldet, er den allerede bred nok, og en søgning må ikke fastgøre
  // den (state.sidebarCollapsed er persisteret).
  if (state.sidebarCollapsed && !hoverOpen.value) state.sidebarCollapsed = false;
  await nextTick();
  searchInput.value?.focus();
}

function closeSearch(): void {
  searchOpen.value = false;
  searchQuery.value = '';
  searchActive.value = 0;
}

function toggleSearch(): void {
  if (searchOpen.value) closeSearch();
  else void openSearch();
}

/** Nulstil markeringen når listen ændrer sig, så Enter altid rammer det øverste. */
watch(searchQuery, () => {
  searchActive.value = 0;
});

function moveSearchActive(delta: number): void {
  const n = searchResults.value.length;
  if (n === 0) return;
  searchActive.value = (searchActive.value + delta + n) % n;
}

async function goToHit(hit: SearchHit): Promise<void> {
  closeSearch();
  // [D-554]: afsnittet følger med som fragment sidst i adressen, og routerens
  // indgang scroller til det. Er vi allerede på siden, push'es der ikke
  // (vue-router afviser identisk navigation), men der scrolles stadig.
  if (route.path !== hit.path) {
    await router.push({ path: hit.path, hash: hit.anchor ? `#${hit.anchor.replace(/^sec-/, '')}` : '' });
  } else if (hit.anchor) {
    goToSection(hit.anchor);
  }
}

function onSearchEnter(): void {
  const hit = searchResults.value[searchActive.value];
  if (hit) void goToHit(hit);
}

/** Escape lukker søgningen uanset hvor fokus er i panelet. */
function onSearchKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeSearch();
    return;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveSearchActive(1);
    return;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveSearchActive(-1);
  }
}

// Skifter ruten (fx via et resultat eller et menupunkt), skal panelet lukke —
// ellers ville menuen blive væk efter navigationen.
watch(
  () => route.path,
  () => {
    if (searchOpen.value) closeSearch();
  },
);

// ---------------------------------------------------------------------------
// HOVER-UDFOLDNING (Charlie 19-10-2026)
// ---------------------------------------------------------------------------
//
// "Når venstremenuen er lukket, skal den ved mouse-over åbne sig automatisk, og
// lukke igen, når man har klikket på en menu, med mindre man har klikket på
// åbne-ikonet, således menuen altid er åben."
//
// Den udfoldede menu lægger sig OVEN PÅ indholdet (kolonnen er absolut over et
// aside der holder 56 px i layoutet), så siden ikke hopper hver gang musen
// strejfer venstre kant. Klik på sidebar-ikonet mens den er udfoldet FASTGØR
// den (state.sidebarCollapsed = false), og så skubber den indholdet som før.
//
// Lukker: når musen forlader menuen, ved klik udenfor, og ved klik på et
// menupunkt der fører til en SIDE. Et klik på en sektion eller gruppe der
// folder en undermenu ud (data-nav-keep-open), holder den åben, ellers kunne
// man aldrig nå længere ned end første underside uden at køre musen ud og ind.
//
// Kun MUS (pointerType): på en tablet ville et tryk på et ikon ellers både
// folde menuen ud og navigere.
//
// Charlie 19-10-2026 (2. runde): *"Hvis menuen er lukket, skal menuen altid
// åbne ved mouse-over, uanset hvor man holder musen over menubjælken."* Derfor
// lytter vi også på pointermove (ikke kun pointerenter: står musen allerede over
// bjælken efter et klik eller en sideindlæsning, kommer der ingen enter), og et
// klik spærrer kun, til musen FLYTTER sig, ikke til den har været ude af
// bjælken. Uden spærringen ville menuen springe ud igen 150 ms efter et klik på
// luk-ikonet, med musen helt stille, og det ville ligne at lukningen svigtede.

/** Musen skal blive over menuen, ikke bare passere forbi på vej til kanten. */
const HOVER_OPEN_DELAY_MS = 150;
/** Et kort smut ud over menuens kant lukker den ikke. */
const HOVER_CLOSE_DELAY_MS = 250;
/** = duration-200 på kolonnens bredde-animation. */
const WIDTH_TRANSITION_MS = 200;

const sidebarEl = ref<HTMLElement | null>(null);
/**
 * Overlay-laget (z-index over sidens sticky header, z-50) bliver stående til
 * bredden har trukket sig ind igen. Ellers ville indholdet male hen over den
 * lukkende menu. Kun mens menuen er udfoldet: permanent ville det løfte den
 * sammenklappede menu op over modalernes mørke baggrund.
 */
const hoverLayer = ref(false);
let hoverTimer: number | undefined;
let layerTimer: number | undefined;
let pointerInside = false;
/** Efter klik på et menupunkt (eller luk-ikonet) åbner menuen først igen, når
 *  musen har FLYTTET sig fra klikkets punkt (mere end SUPPRESS_MOVE_PX). */
let suppressedAt: { x: number; y: number } | null = null;
const SUPPRESS_MOVE_PX = 3;

/** Skygge kun mens menuen ligger over indholdet. */
const hoverOverlay = computed(() => hoverOpen.value && pinnedCollapsed.value);

const toggleLabel = computed(() => {
  if (hoverOverlay.value) return 'Hold menuen åben';
  return collapsed.value ? 'Åbn menuen' : 'Luk menuen';
});

function clearHoverTimer(): void {
  if (hoverTimer != null) window.clearTimeout(hoverTimer);
  hoverTimer = undefined;
}

function dropLayerAfterTransition(): void {
  if (layerTimer != null) window.clearTimeout(layerTimer);
  layerTimer = window.setTimeout(() => {
    layerTimer = undefined;
    if (!hoverOpen.value) hoverLayer.value = false;
  }, WIDTH_TRANSITION_MS);
}

function openHover(): void {
  hoverTimer = undefined;
  if (!pinnedCollapsed.value) return;
  if (layerTimer != null) window.clearTimeout(layerTimer);
  layerTimer = undefined;
  hoverLayer.value = true;
  hoverOpen.value = true;
}

function closeHover(): void {
  clearHoverTimer();
  if (!hoverOpen.value) return;
  hoverOpen.value = false;
  if (searchOpen.value) closeSearch();
  dropLayerAfterTransition();
}

/** Planlæg udfoldning, hvis menuen er lukket og musen må åbne den. */
function scheduleHoverOpen(event: PointerEvent): void {
  if (hoverOpen.value || !pinnedCollapsed.value || hoverTimer != null) return;
  if (suppressedAt) {
    const moved = Math.hypot(event.clientX - suppressedAt.x, event.clientY - suppressedAt.y);
    if (moved <= SUPPRESS_MOVE_PX) return;
    suppressedAt = null;
  }
  hoverTimer = window.setTimeout(openHover, HOVER_OPEN_DELAY_MS);
}

function onSidebarPointerEnter(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') return;
  pointerInside = true;
  clearHoverTimer();
  scheduleHoverOpen(event);
}

function onSidebarPointerMove(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') return;
  pointerInside = true;
  scheduleHoverOpen(event);
}

function onSidebarPointerLeave(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') return;
  pointerInside = false;
  suppressedAt = null;
  clearHoverTimer();
  // Søgningen holder menuen åben: man skriver i feltet og kører gerne musen
  // væk imens. Den lukker med søgningen (watch nedenfor) eller ved klik udenfor.
  if (!hoverOpen.value || searchOpen.value) return;
  hoverTimer = window.setTimeout(closeHover, HOVER_CLOSE_DELAY_MS);
}

/** Klik på et menupunkt i den hover-udfoldede menu (event-delegering). */
function onSidebarClick(event: MouseEvent): void {
  if (!hoverOpen.value) return;
  // Ctrl/⌘-klik åbner i ny fane: man bliver hvor man er, og menuen med.
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  const hit = (event.target as Element | null)?.closest('a, [data-nav-close]');
  if (!hit || hit.hasAttribute('data-nav-keep-open')) return;
  suppressedAt = { x: event.clientX, y: event.clientY };
  closeHover();
}

function onDocumentPointerDown(event: PointerEvent): void {
  if (!hoverOpen.value) return;
  if (sidebarEl.value?.contains(event.target as Node)) return;
  closeHover();
}

watch(searchOpen, (open) => {
  if (!open && hoverOpen.value && !pointerInside) closeHover();
});

onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown));
onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  clearHoverTimer();
  if (layerTimer != null) window.clearTimeout(layerTimer);
});

/**
 * Ikonet OG logoet "Cockpit" (samme knap) styrer den PERMANENTE tilstand
 * (Charlie 19-10-2026): lukket (også når den er hover-udfoldet) → klik åbner
 * permanent; åben → klik lukker permanent.
 */
function toggleCollapsed(event: MouseEvent): void {
  clearHoverTimer();
  // Hover-udfoldet: klik på ikonet FASTGØR menuen åben. I et for smalt vindue
  // kan den ikke fastgøres (tvangs-kollaps); valget huskes til vinduet bliver
  // bredt nok, og menuen bliver liggende som overlay til musen går.
  if (hoverOpen.value) {
    state.sidebarCollapsed = false;
    if (!viewportTooNarrow.value) {
      hoverOpen.value = false;
      dropLayerAfterTransition();
    }
    return;
  }
  state.sidebarCollapsed = !state.sidebarCollapsed;
  // Lukket med musen over menuen: den springer først ud igen, når musen flytter sig.
  if (state.sidebarCollapsed) suppressedAt = { x: event.clientX, y: event.clientY };
}

/**
 * Agenter-menupunktets loading-dots ([D-166]): tre animerede prikker til
 * højre for "Agenter" når mindst én agent kører (natjobs m. heartbeat +
 * sync-kaskaden). Letvægts-poll hvert 30. sekund — kun når brugeren har
 * adgang til siden (samme grant-gating som selve menupunktet).
 */
const agentsRunning = ref(false);
let agentsPollTimer: number | undefined;

async function pollAgentsRunning(): Promise<void> {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  // ⚠ Vent på side-KATALOGET: før det er hentet svarer canAccessPath fail-open
  //   på ukendte ruter, så en bruger UDEN agent-adgang fik ét 403-kald i
  //   konsollen ved hvert side-load (fanget af [D-229]-E2E'ens konsol-tjek).
  if (!auth.user || !permissionsState.loaded || !canAccessPath('/doc/agents', roles)) {
    agentsRunning.value = false;
    return;
  }
  try {
    const res = await fetch('/api/docs/agents/running', { credentials: 'same-origin' });
    if (!res.ok) {
      agentsRunning.value = false;
      return;
    }
    const data = (await res.json()) as { running?: boolean };
    agentsRunning.value = data.running === true;
  } catch {
    agentsRunning.value = false;
  }
}

/**
 * Til gennemgang-badgen ([D-237]): antal SEO-forslag der venter på
 * gennemgang, vist som tal i en cirkel på menupunktet (og på SEO-gruppen, så
 * tallet også ses uden for niveau 3-kolonnen). Samme letvægts-poll-mønster
 * som Agenter-prikkerne: hvert 30. sekund, kun med grant på siden.
 */
const seoReviewOpen = ref(0);
let seoReviewPollTimer: number | undefined;

async function pollSeoReviewOpen(): Promise<void> {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  // Samme værn som Agenter-pollen: vent på side-kataloget, ellers giver
  // fail-open et 403-kald i konsollen for brugere uden adgang.
  if (!auth.user || !permissionsState.loaded || !canAccessPath('/webshop/seo/review', roles)) {
    seoReviewOpen.value = 0;
    return;
  }
  try {
    const res = await fetch('/api/webshop/seo/review/open-count', { credentials: 'same-origin' });
    if (!res.ok) {
      seoReviewOpen.value = 0;
      return;
    }
    const data = (await res.json()) as { open?: number };
    seoReviewOpen.value = typeof data.open === 'number' && data.open > 0 ? data.open : 0;
  } catch {
    seoReviewOpen.value = 0;
  }
}

/** Billeder · Til gennemgang-badgen ([D-327]) — samme mønster som SEO-badgen. */
const imagesReviewOpen = ref(0);
let imagesReviewPollTimer: number | undefined;

async function pollImagesReviewOpen(): Promise<void> {
  const roles = (auth.user?.roles ?? []) as RoleCode[];
  if (!auth.user || !permissionsState.loaded || !canAccessPath('/webshop/images/review', roles)) {
    imagesReviewOpen.value = 0;
    return;
  }
  try {
    const res = await fetch('/api/webshop/images/review/open-count', { credentials: 'same-origin' });
    if (!res.ok) {
      imagesReviewOpen.value = 0;
      return;
    }
    const data = (await res.json()) as { open?: number };
    imagesReviewOpen.value = typeof data.open === 'number' && data.open > 0 ? data.open : 0;
  } catch {
    imagesReviewOpen.value = 0;
  }
}

onMounted(() => {
  startAiActivityPolling();
  void pollAgentsRunning();
  agentsPollTimer = window.setInterval(() => void pollAgentsRunning(), 30_000);
  void pollSeoReviewOpen();
  seoReviewPollTimer = window.setInterval(() => void pollSeoReviewOpen(), 30_000);
  void pollImagesReviewOpen();
  imagesReviewPollTimer = window.setInterval(() => void pollImagesReviewOpen(), 30_000);
});

// Auth OG side-kataloget bootstrapper asynkront: ved mount er begge typisk
// endnu ikke klar, så første poll returnerer tomt — re-poll når de er, ellers
// er prikkerne blinde i op til 30 s efter login/refresh (fanget af
// E2E-kontra-testen).
watch(
  () => [auth.user, permissionsState.loaded] as const,
  () => {
    void pollAgentsRunning();
    void pollSeoReviewOpen();
    void pollImagesReviewOpen();
  },
);

onUnmounted(() => {
  if (agentsPollTimer != null) window.clearInterval(agentsPollTimer);
  if (seoReviewPollTimer != null) window.clearInterval(seoReviewPollTimer);
  if (imagesReviewPollTimer != null) window.clearInterval(imagesReviewPollTimer);
});

/**
 * AI arbejder ([D-439], §95): 3 lysegrå prikker ud for hovedmenuen, undermenuen
 * og siden, hvor en sprogmodel- eller billedkørsel hører hjemme. Grupper
 * (hovedmenu, niveau 2 m. børn, Driftstatus) matcher alt under stien; et punkt
 * der ER en side, matcher kun sig selv (`exact`). Kilden er den fælles poll i
 * useAiActivity, så sidebaren og sidens header altid er enige.
 */
function aiOn(path: string, exact = false): boolean {
  return aiActivityForMenu(path, exact).length > 0;
}
function aiTip(path: string, exact = false): string {
  return aiActivityTip(aiActivityForMenu(path, exact));
}

/** Manuel sync fra Driftstatus-arket: arket lukker, kaskade-modalen åbner. */
function openSyncFromSheet(): void {
  closeMobileSheet();
  syncCascadeOpen.value = true;
}
</script>

<template>
  <!-- Desktop og tablet (md og op). Under md tager footer-menuen nedenfor over. -->
  <!-- Aside'ets bredde er LAYOUT-pladsen (fastgjort valg), kolonnens bredde er
       det man SER: hover-udfoldet (Charlie 19-10-2026) holder aside'et 56 px,
       mens kolonnen lægger sig over indholdet med skygge. -->
  <aside
    ref="sidebarEl"
    class="sticky top-0 h-screen relative hidden md:flex flex-row shrink-0 print:hidden transition-[width] duration-200 ease-out"
    :class="[pinnedCollapsed ? 'w-14' : searchOpen ? 'w-80' : 'w-56', hoverLayer ? 'z-[52]' : '']"
    data-testid="sidebar"
    @pointerenter="onSidebarPointerEnter"
    @pointermove="onSidebarPointerMove"
    @pointerleave="onSidebarPointerLeave"
    @click="onSidebarClick"
  >
    <!-- Hovedkolonnen (niveau 1+2, og niveau 3 inde i den aktive gruppes boks). -->
    <div
      class="absolute inset-y-0 left-0 flex flex-col bg-page transition-[width,box-shadow] duration-200 ease-out"
      :class="[collapsed ? 'w-14' : searchOpen ? 'w-80' : 'w-56', hoverOverlay ? 'shadow-xl dark:shadow-black/60' : '']"
      :data-hover-open="hoverOverlay ? 'true' : undefined"
      data-testid="sidebar-column"
    >

    <!-- Sticky topbar: sidebar-toggle + brand-wordmark på samme linje.
         Kollaps-knappen bruger samme layout-mønster (px-2 + gap-3 + w-5 h-5
         ikon) som nav-items, så Cockpit-teksten venstrejusteres præcist med
         "Marketing" / "Webshop" osv. nedenfor. Ved åben menu står tema-
         togglen (husets SegmentToggle m. små sol/måne-ikon-segmenter,
         Charlie 07-08) i rækkens højre kant som flex-søskende til knappen.
         pb-8 bevarer afstand ned til nav-items. -->
    <div class="shrink-0 pt-2 pb-8">
      <div class="flex items-center gap-2 mx-2">
        <button
          type="button"
          class="flex items-center h-10 gap-3 px-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors flex-1 min-w-0"
          :aria-label="toggleLabel"
          v-tip="toggleLabel"
          data-testid="sidebar-toggle"
          @click="toggleCollapsed"
        >
          <span class="block w-5 h-5 shrink-0" v-html="ICONS.sidebar" />
          <span
            v-if="!collapsed"
            class="text-lg font-semibold text-ink whitespace-nowrap tracking-tight"
          >
            Cockpit
          </span>
        </button>
        <!-- Global søgning ([D-198]): lupen står hvor tema-togglen lå, til
             højre for wordmarket. Klik udvider draweren og erstatter
             menupunkterne med resultater. -->
        <button
          v-if="!collapsed"
          type="button"
          class="shrink-0 w-7 h-7 grid place-items-center rounded-lg transition-colors"
          :class="searchOpen ? 'bg-subtle text-ink' : 'text-muted hover:text-ink hover:bg-hover'"
          :aria-label="searchOpen ? 'Luk søgning' : 'Søg i Cockpit'"
          :aria-expanded="searchOpen"
          v-tip="searchOpen ? 'Luk søgning' : 'Søg efter sider, afsnit, nøgletal og ord'"
          @click="toggleSearch"
        >
          <span class="block w-4 h-4" v-html="searchOpen ? ICONS.close : ICONS.search" />
        </button>
      </div>
    </div>

    <!-- Scrollable area: primær nav + bund-gruppe (Driftstatus + synkroniser).
         Scrollbar er skjult via .scrollbar-none. Hvis viewport er højt nok
         til at vise alt, skubber flex-1-spaceren bund-gruppen ned mod bunden.
         Hvis viewport er for lavt, kan brugeren scrolle lodret. -->
    <div class="flex-1 overflow-y-auto scrollbar-none flex flex-col">
      <!-- SØGE-PANEL ([D-198]): træder i menupunkternes plads når lupen er
           aktiv. Menuen er skjult (v-else nedenfor), ikke overlejret, præcis
           som Charlie bad om — draweren er bredere så resultat-linjerne kan
           bære både titel og kontekst. -->
      <div v-if="searchOpen && !collapsed" class="flex flex-col min-h-0" @keydown="onSearchKeydown">
        <div class="px-2 pb-2">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            placeholder="Søg efter side, afsnit eller nøgletal"
            class="w-full px-2.5 py-1.5 rounded-lg border border-line bg-surface text-[13px] text-ink placeholder:text-faint focus:outline-none focus:ring-1 focus:ring-ink/20"
            aria-label="Søg i Cockpit"
            @keydown.enter.prevent="onSearchEnter"
          />
          <p v-if="searchQuery.trim().length < 2" class="mt-2 px-0.5 text-[11px] text-faint leading-snug">
            Skriv mindst 2 bogstaver. Søgningen finder sider, afsnit, nøgletal og
            opslag i ordbogen, og viser kun det du har adgang til.
          </p>
        </div>

        <p v-if="searchEmpty" class="px-2.5 text-[12px] text-muted">
          Ingen træf på "{{ searchQuery.trim() }}".
        </p>

        <nav v-if="searchResults.length > 0" class="flex flex-col pb-2" aria-label="Søgeresultater">
          <button
            v-for="(hit, i) in searchResults"
            :key="`${hit.path}|${hit.anchor ?? ''}|${hit.title}`"
            type="button"
            class="shrink-0 text-left px-2.5 py-1.5 mx-2 rounded-lg transition-colors"
            :class="i === searchActive ? 'bg-subtle' : 'hover:bg-hover'"
            data-nav-close
            @click="goToHit(hit)"
            @mouseenter="searchActive = i"
          >
            <span class="block text-[13px] font-medium text-ink truncate">{{ hit.title }}</span>
            <span class="block text-[11px] text-faint truncate">
              {{ kindLabel(hit.kind) }} · {{ hit.context }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Primær navigation -->
      <nav v-if="!(searchOpen && !collapsed)" class="flex flex-col py-1">
        <template v-for="item in visibleNavItems" :key="item.path">
          <!-- Hovedmenu-link: navigerer til første undermenu hvis sektionen
               har undermenuer (deterministisk landing-side).
               Active-baggrund applies KUN hvis sektionen er aktiv OG den ikke
               har undermenuer — hvis den har undermenuer, er det den aktive
               undermenu (længere nede) der bærer active-highlightingen.
               Tekst-farve skifter dog stadig til text-ink når sektionen er
               aktiv, så hovedmenu-labelen ikke bliver gråtonet. -->
          <!-- data-nav-keep-open: en sektion MED undermenu holder den
               hover-udfoldede menu åben, så man kan vælge videre i den. -->
          <RouterLink
            :to="resolveMainPath(item)"
            :data-nav-keep-open="item.subItems ? '' : undefined"
            class="relative shrink-0 flex items-center h-10 gap-3 transition-colors mx-2 px-2 rounded-lg"
            :class="[
              isSectionActive(item.path)
                ? 'text-ink'
                : isEmptyItem(item)
                  ? 'text-faint hover:text-muted'
                  : 'text-muted hover:text-ink',
              isSectionActive(item.path) && !item.subItems ? 'bg-subtle' : 'hover:bg-hover',
            ]"
          >
            <span class="block w-5 h-5 shrink-0" v-html="ICONS[item.icon]" />
            <!-- §29: for langt navn klippes med … frem for at flyde ud af
                 sidebaren. min-w-0 skal med, ellers nægter flex-barnet at
                 krympe og truncate får aldrig noget at klippe. -->
            <span v-if="!collapsed" class="text-[13px] font-medium truncate min-w-0">
              {{ item.label }}
            </span>
            <!-- AI arbejder ([D-439], §95): ud for hovedmenuen; kollapset under ikonet. -->
            <AiDots
              v-if="aiOn(item.path)"
              :class="collapsed ? 'absolute bottom-0.5 left-[18px] -translate-x-1/2' : ''"
              :tip="aiTip(item.path)"
              data-testid="ai-activity-nav"
            />
          </RouterLink>

          <!-- Undermenuer: vises indrykket kun når sektionen er aktiv og
               sidebar er udvidet. Lodret 1px border til venstre er
               positioneret så den er centreret ud for hovedmenuens ikoner
               (px-4 = 16px padding + halv ikon-bredde w-5/2 = 10px = 26px). -->
          <div
            v-if="item.subItems && isSectionActive(item.path) && !collapsed"
            class="relative py-0.5"
          >
            <div
              class="absolute top-1 bottom-1 w-px bg-line"
              style="left: 26px"
              aria-hidden="true"
            ></div>
            <!-- Undermenu-text venstrekantes med hovedmenu-text (48 px fra
                 sidebar-kant: mx-2 = 8 + pl-10 = 40). Samme font-size (13px)
                 + font-medium + text-muted/text-ink-farve som hovedmenu. -->
            <!-- Grupper m. niveau 3 navigerer til første barn og bærer selv
                 active-stilen så længe ruten ligger i gruppen (RouterLinks
                 active-class rammer kun det konkrete barn). -->
            <!-- Sektions-RODEN som undermenupunkt (fx Butikker → Åbningstider
                 på /stores; Marketing havde det til [D-198]) skal KUN være
                 markeret på præcis den rute: RouterLinks active-class er
                 prefix-baseret og ville ellers highlighte roden samtidig med
                 hver eneste underside ([D-183]-fund, rettet generelt). -->
            <template v-for="sub in item.subItems" :key="sub.path">
              <!-- ÅBEN GRUPPE M. NIVEAU 3 ([D-321], Charlie 26-09-2026): siderne
                   vises INDE i den markerede niveau 2-boks, samme tekststørrelse,
                   i stedet for i en ekstra kolonne der åd sidebredde.
                   [D-326] (Charlie 27-09-2026): al menutekst står på SAMME
                   venstrekant (48 px: boksens mx-2 + pl-10, som niveau 1's ikon
                   + gap og niveau 2's pl-10). Niveau 2-navnet er grå tekst, så
                   det læses som gruppens overskrift og ikke som et punkt, og det
                   er niveau 3-punktet der bærer markeringen, fra kant til kant i
                   boksen (overflow-hidden klipper båndet til boksens hjørner).
                   Charlie 27-09-2026: boksen er en tynd RAMME uden baggrund, og
                   uden bund-luft, så det sidste punkts hover/markering flugter
                   med boksens bund langs de runde hjørner. Rammens 1 px er
                   trukket fra indrykningen (pl-[39px]), så teksten bliver på
                   48 px; barnet markeres som alle andre aktive menupunkter
                   (bg-subtle), hover = bg-hover. `relative bg-page` er
                   sidebarens EGEN flade (ingen synlig farve): den dækker kun
                   niveau 2's lodrette hjælpelinje, der ellers krydsede rammen. -->
              <div
                v-if="sub.children && groupIsActive(sub)"
                class="relative mx-2 my-0.5 rounded-lg border border-line bg-page overflow-hidden"
                data-testid="nav-group-open"
              >
                <RouterLink
                  :to="resolveSubPath(sub)"
                  class="shrink-0 flex items-center h-8 pl-[39px] pr-2 text-faint hover:text-muted transition-colors"
                  active-class=""
                  exact-active-class=""
                  data-nav-keep-open
                  data-testid="nav-group-heading"
                >
                  <span class="text-[13px] font-medium truncate min-w-0">{{ sub.label }}</span>
                  <AiDots v-if="aiOn(sub.path)" class="ml-1.5" :tip="aiTip(sub.path)" data-testid="ai-activity-nav" />
                  <span
                    v-if="sub.path === '/webshop/seo' && seoReviewOpen > 0"
                    class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                    v-tip="`${seoReviewOpen} forslag venter på gennemgang`"
                    data-testid="seo-review-badge"
                  >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                  <span
                    v-if="sub.path === '/webshop/images' && imagesReviewOpen > 0"
                    class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                    v-tip="`${imagesReviewOpen} billeder venter på gennemgang`"
                    data-testid="images-review-badge"
                  >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                </RouterLink>
                <!-- §29: dynamiske punkter (analysernes lange titler) bærer hele
                     navnet som tooltip — et klippet navn skal stadig kunne læses. -->
                <RouterLink
                  v-for="child in sub.children"
                  :key="child.path"
                  :to="child.path"
                  class="shrink-0 flex items-center h-8 pl-[39px] pr-2 transition-colors text-muted hover:text-ink hover:bg-hover"
                  :class="child.path === activeCampaignMenuPath ? 'bg-subtle !text-ink' : ''"
                  active-class="bg-subtle !text-ink"
                  data-testid="nav-group-child"
                >
                  <span
                    class="text-[13px] font-medium truncate min-w-0"
                    v-tip="item.dynamicPrefix ? child.label : ''"
                  >{{ child.label }}</span>
                  <AiDots v-if="aiOn(child.path, true)" class="ml-1.5" :tip="aiTip(child.path, true)" data-testid="ai-activity-nav" />
                  <!-- Til gennemgang-badgen ([D-237]): antal forslag der venter. -->
                  <span
                    v-if="child.path === '/webshop/seo/review' && seoReviewOpen > 0"
                    class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                    v-tip="`${seoReviewOpen} forslag venter på gennemgang`"
                    data-testid="seo-review-badge-l3"
                  >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                  <span
                    v-if="child.path === '/webshop/images/review' && imagesReviewOpen > 0"
                    class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                    v-tip="`${imagesReviewOpen} billeder venter på gennemgang`"
                    data-testid="images-review-badge-l3"
                  >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                </RouterLink>
              </div>
              <RouterLink
                v-else
                :to="resolveSubPath(sub)"
                :data-nav-keep-open="sub.children ? '' : undefined"
                class="shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg transition-colors"
                :active-class="sub.children || sub.path === item.path ? '' : 'bg-subtle !text-ink'"
                :exact-active-class="!sub.children && sub.path === item.path ? 'bg-subtle !text-ink' : ''"
                :class="subIsFaint(item, sub) ? 'text-faint hover:text-muted' : 'text-muted hover:text-ink'"
              >
                <!-- §29: dynamiske punkter bærer hele navnet som tooltip — et
                     klippet navn skal stadig kunne læses. Statiske punkter er
                     korte og får ingen, så tooltippen ikke bare gentager det man
                     allerede kan se. [D-289]: kategorierne er korte og faste. -->
                <span
                  class="text-[13px] font-medium truncate min-w-0"
                  v-tip="item.dynamicPrefix && !sub.children ? sub.label : ''"
                >{{ sub.label }}</span>
                <!-- AI arbejder ([D-439], §95). Agenter har sine egne (blå) prikker
                     for alle agenter; de grå vises ikke oven i dem. -->
                <AiDots
                  v-if="aiOn(sub.path, !sub.children) && !(sub.path === '/doc/agents' && agentsRunning)"
                  class="ml-1.5"
                  :tip="aiTip(sub.path, !sub.children)"
                  data-testid="ai-activity-nav"
                />
                <!-- Til gennemgang-badgen ([D-237]) på SEO-gruppen: tallet skal
                     kunne ses allerede på niveau 2, før gruppen er åben. Amber =
                     "venter på menneskelig handling" (§40). -->
                <span
                  v-if="sub.path === '/webshop/seo' && seoReviewOpen > 0"
                  class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                  v-tip="`${seoReviewOpen} forslag venter på gennemgang`"
                  data-testid="seo-review-badge"
                >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                <span
                  v-if="sub.path === '/webshop/images' && imagesReviewOpen > 0"
                  class="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold flex items-center justify-center"
                  v-tip="`${imagesReviewOpen} billeder venter på gennemgang`"
                  data-testid="images-review-badge"
                >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                <span
                  v-if="sub.children"
                  :class="(sub.path === '/webshop/seo' && seoReviewOpen > 0) || (sub.path === '/webshop/images' && imagesReviewOpen > 0) ? 'ml-1' : 'ml-auto'"
                  class="shrink-0 text-faint text-[10px]"
                  aria-hidden="true"
                >›</span>
                <!-- Agenter ([D-166]): tre animerede prikker når ≥1 agent kører. -->
                <span
                  v-if="sub.path === '/doc/agents' && agentsRunning"
                  class="ml-auto shrink-0 flex items-center gap-0.5 text-sky-600 dark:text-sky-400"
                  v-tip="'Én eller flere agenter kører lige nu'"
                >
                  <span class="agent-dot" style="animation-delay: 0ms"></span>
                  <span class="agent-dot" style="animation-delay: 150ms"></span>
                  <span class="agent-dot" style="animation-delay: 300ms"></span>
                </span>
              </RouterLink>
            </template>
          </div>
        </template>
      </nav>

      <!-- Spacer: skubber Driftstatus/Sync ned i bunden af scroll-området når der
           er plads; kollapser til 0 og lader dem scrolle med når viewport er lavt. -->
      <div class="flex-1"></div>

      <!-- DRIFTSTATUS-GRUPPEN (Charlies beslutning 18-08-2026): Systemlog og
           Sync data er flyttet IND UNDER Driftstatus, som dermed får TRE
           undersider — Kørsler, Log og Manuel sync (Charlies navne 18-08:
           punkterne skal ikke gentage sektionens navn). Systemlog lå før
           fladt under Indstillinger, og Sync data var et hovedpunkt for sig her
           i bunden. De tre svarer på samme spørgsmål i tre zoom-niveauer: kører
           jobbene, hvad gjorde de præcist, og kør dem igen nu.

           Gruppen bliver liggende i BUNDEN frem for at blive en niveau-3-gruppe
           inde under Indstillinger, fordi Sync data skal kunne nås med ét klik
           uanset hvilken side man står på. En niveau-3-kolonne vises kun når man
           allerede er inde i gruppen.

           Selve siden Driftstatus står som FØRSTE underpunkt — samme mønster som
           Kunder → Kunder og Butikker → Åbningstider i hovedmenuen: overskriften
           er gruppen, punktet er siden. Overskriften navigerer derfor til første
           synlige underpunkt, ikke nødvendigvis til /ops/jobs.

           [D-222] (20-08-2026): stierne fulgte efter menuen ud af Indstillinger
           — /settings/ops → /ops/jobs og /settings/ops/log → /ops/log. Begge
           gamle stier redirecter, og grants blev kopieret FØR deploy (§21).

           ⚠ Hvert punkt har sit EGET grant, og gruppen retter sig efter det.
           Har man kun Systemlog (som Per skal have), er der ét underpunkt, og
           overskriften linker dertil — aldrig til en side man får 403 på. Har
           man intet af det, findes hele gruppen ikke.

           Scroller MED navigationen (IKKE sticky), på lige fod med
           nav-punkterne. Tema er flyttet til Indstillinger → Min profil og Log
           ud til brugerinfo-rækken ([D-155]). -->
      <template v-if="opsGroupVisible">
        <!-- Overskriften bærer selv active-farven når man står et sted i
             gruppen (som hovedmenuens sektioner), mens baggrunds-markeringen
             hører til det konkrete underpunkt. -->
        <RouterLink
          v-if="opsGroupTarget"
          :to="opsGroupTarget"
          data-nav-keep-open
          class="relative shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg transition-colors hover:bg-hover"
          :class="isSectionActive('/ops') ? 'text-ink' : 'text-muted hover:text-ink'"
          aria-label="Driftstatus for de automatiske kørsler"
        >
          <span class="block w-5 h-5 shrink-0" v-html="ICONS.activity" />
          <span v-if="!collapsed" class="text-[13px] font-medium truncate min-w-0">
            Driftstatus
          </span>
          <AiDots
            v-if="aiOn('/ops')"
            :class="collapsed ? 'absolute bottom-0.5 left-[18px] -translate-x-1/2' : 'ml-auto'"
            :tip="aiTip('/ops')"
            data-testid="ai-activity-nav"
          />
        </RouterLink>
        <!-- Ingen af de to sider må ses (kun Sync data): så er overskriften en
             ren etiket. Et link til en 403 er værre end intet link. -->
        <div
          v-else
          class="shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg text-muted cursor-default"
        >
          <span class="block w-5 h-5 shrink-0" v-html="ICONS.activity" />
          <span v-if="!collapsed" class="text-[13px] font-medium truncate min-w-0">
            Driftstatus
          </span>
        </div>

        <!-- Undermenuen: samme lodrette 1px-linje ved 26px og samme pl-10 som
             hovedmenuens undermenuer, så de to niveauer ser ens ud overalt
             (UI-konsistens — ingen per-sted-varianter). -->
        <div v-if="!collapsed && opsGroupOpen" class="relative py-0.5">
          <div
            class="absolute top-1 bottom-1 w-px bg-line"
            style="left: 26px"
            aria-hidden="true"
          ></div>

          <!-- Selve Kørsler-siden. exact-active BLIVER stående, selv om [D-222]
               fjernede grunden: '/settings/ops' VAR et prefix af
               '/settings/ops/log', så punktet stod markeret samtidig med
               Systemlog ([D-183]-fundet, samme fælde). '/ops/jobs' er ikke et
               prefix af '/ops/log', men exact-active er stadig det rigtige for
               et punkt uden undersider — og værnet koster ingenting. -->
          <RouterLink
            v-if="canSeeOps"
            to="/ops/jobs"
            class="shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            active-class=""
            exact-active-class="bg-subtle !text-ink"
          >
            <span class="text-[13px] font-medium truncate min-w-0">Kørsler</span>
          </RouterLink>

          <RouterLink
            v-if="canSeeSystemLog"
            to="/ops/log"
            class="shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            active-class="bg-subtle !text-ink"
          >
            <span class="text-[13px] font-medium truncate min-w-0">Log</span>
          </RouterLink>

          <!-- Platformvagtens fund ([D-402]). -->
          <RouterLink
            v-if="canSeeOpsActions"
            to="/ops/actions"
            class="shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            active-class="bg-subtle !text-ink"
          >
            <span class="text-[13px] font-medium truncate min-w-0">Handlinger</span>
            <AiDots v-if="aiOn('/ops/actions', true)" class="ml-1.5" :tip="aiTip('/ops/actions', true)" data-testid="ai-activity-nav" />
          </RouterLink>

          <!-- Driftsforstyrrelser ([D-418]). -->
          <RouterLink
            v-if="canSeeOpsDisruptions"
            to="/ops/disruptions"
            class="shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            active-class="bg-subtle !text-ink"
          >
            <span class="text-[13px] font-medium truncate min-w-0">Driftsforstyrrelser</span>
          </RouterLink>

          <!-- [D-136]-opfølgning: redigering kræver canEdit (serveren håndhæver 403) -->
          <button
            v-if="canSyncData"
            type="button"
            class="w-[calc(100%-1rem)] shrink-0 flex items-center h-8 pl-10 pr-2 mx-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            aria-label="Åbn sync data cascade"
            data-nav-close
            @click="syncCascadeOpen = true"
          >
            <span class="text-[13px] font-medium truncate min-w-0">Manuel sync</span>
          </button>
        </div>

        <!-- Kollapset sidebar: undermenuer har ingen tekst at rykke ind, så
             punkterne står som ikoner på linje med overskriften. Driftstatus
             selv er overskriftens ikon og gentages ikke.
             Samme åbne/lukkede regel som udvidet — ellers ville de tre punkter
             stå permanent i bunden af en kollapset menu, altså præcis det
             Charlie bad om at komme af med. Sync data er dermed to klik væk i
             kollapset tilstand (åbn Driftstatus, klik Sync data), men den
             forsvinder ikke. -->
        <template v-if="collapsed && opsGroupOpen">
          <RouterLink
            v-if="canSeeSystemLog"
            to="/ops/log"
            class="shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            aria-label="Log"
            v-tip="'Log'"
          >
            <span class="block w-5 h-5 shrink-0" v-html="ICONS.scrollText" />
          </RouterLink>
          <RouterLink
            v-if="canSeeOpsActions"
            to="/ops/actions"
            class="relative shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            aria-label="Handlinger"
            v-tip="'Handlinger'"
          >
            <span class="block w-5 h-5 shrink-0" v-html="ICONS.shieldCheck" />
            <AiDots v-if="aiOn('/ops/actions', true)" class="absolute bottom-0.5 left-[18px] -translate-x-1/2" data-testid="ai-activity-nav" />
          </RouterLink>
          <RouterLink
            v-if="canSeeOpsDisruptions"
            to="/ops/disruptions"
            class="shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            aria-label="Driftsforstyrrelser"
            v-tip="'Driftsforstyrrelser'"
          >
            <span class="block w-5 h-5 shrink-0" v-html="ICONS.triangleAlert" />
          </RouterLink>
          <button
            v-if="canSyncData"
            type="button"
            class="shrink-0 flex items-center h-10 gap-3 mx-2 px-2 rounded-lg text-muted hover:text-ink hover:bg-hover transition-colors"
            aria-label="Åbn sync data cascade"
            v-tip="'Manuel sync'"
            @click="syncCascadeOpen = true"
          >
            <span class="block w-5 h-5 shrink-0" v-html="ICONS.refresh" />
          </button>
        </template>
      </template>
    </div>

    <!-- Sticky brugerinfo: ligger UDEN FOR scroll-området (shrink-0), så avatar/navn/
         email altid er synlige i bunden. Driftstatus-gruppen ovenfor scroller med
         menuen.
         Skillelinjen over brugernavnet er FJERNET (Charlie 18-08-2026): den
         markerede en opdeling der ikke længere var noget værd, efter at
         Driftstatus samlede sine punkter og menuens bund blev rolig. Fladt uden
         streger er husets udgangspunkt ([D-021]); en linje skal fortjene sin
         plads. -->
    <div v-if="auth.user" class="shrink-0 pt-1 pb-2">
      <!-- Profilbillede (Graph) med initial-fallback + rigtigt navn + Log ud-ikon
           til højre for navnet (ikon-only, [D-155] — teksten "Log ud" er droppet).
           Kollapset vises kun avataren, centreret. -->
      <div class="flex items-center gap-3 mx-2 px-2 py-1.5">
        <img
          v-if="auth.user.avatarUrl"
          :src="auth.user.avatarUrl"
          :alt="userName"
          class="shrink-0 w-5 h-5 rounded-full object-cover ring-1 ring-line"
        />
        <span
          v-else
          class="shrink-0 w-5 h-5 rounded-full bg-subtle text-ink ring-1 ring-line grid place-items-center text-[9px] font-semibold select-none"
          :title="auth.user.displayName ?? auth.user.email"
          aria-hidden="true"
        >
          {{ userInitials }}
        </span>
        <div v-if="!collapsed" class="min-w-0 flex-1 leading-tight">
          <p class="text-[13px] font-light text-ink truncate" :title="userName">
            {{ userName }}
          </p>
        </div>
        <!-- Tema-toggle ([D-198]): ét ikon, lige til venstre for logud. Ikonet
             viser hvad man skifter TIL — måne i lyst tema, sol i mørkt. -->
        <button
          v-if="!collapsed"
          type="button"
          class="shrink-0 w-5 h-5 grid place-items-center text-muted hover:text-ink transition-colors"
          :aria-label="themeToggleLabel"
          v-tip="themeToggleLabel"
          @click="toggleTheme"
        >
          <span class="block w-4 h-4" v-html="themeIsDark ? ICONS.sun : ICONS.moon" />
        </button>
        <button
          v-if="!collapsed"
          type="button"
          class="shrink-0 w-5 h-5 grid place-items-center text-muted hover:text-ink transition-colors"
          aria-label="Log ud"
          v-tip="'Log ud'"
          @click="onLogout"
        >
          <span class="block w-4 h-4" v-html="logoutIcon" />
        </button>
      </div>
    </div>

    <!-- Sync-cascade modal — teleportet til <body> via SyncCascade's egen
         Teleport-direktiv så z-index ikke konflikter med sidebar-stacking. -->
    <SyncCascade :open="syncCascadeOpen" @close="syncCascadeOpen = false" />
    </div>

    <!-- Niveau 3-KOLONNEN er udgået ([D-321], Charlie 26-09-2026): undersiderne
         står nu inde i den markerede niveau 2-boks i hovedkolonnen ovenfor, så
         menuen ikke æder en ekstra kolonnes bredde fra indholdet. -->
  </aside>

  <!-- MOBILENS FOOTER-MENU (Charlie 25-09-2026), kun under md. Logikken står i
       script-delen; tegningen her bruger de samme grants og den samme aktive
       sektion som sidebaren. z-[45]: over tabellernes sticky thead (30) og
       listerne (40), under drawerne (50) og tooltips (100). -->
  <nav
    class="md:hidden print:hidden fixed inset-x-0 bottom-0 z-[45] bg-surface/95 backdrop-blur border-t border-line/60 pb-[env(safe-area-inset-bottom)]"
    aria-label="Hovedmenu"
    data-testid="mobile-nav"
  >
    <div
      ref="mobileNavScroller"
      class="flex h-16 items-stretch overflow-x-auto scrollbar-none snap-x snap-mandatory px-1"
    >
      <button
        type="button"
        class="mnav-item"
        :class="mobileSheet === 'search' ? 'text-ink' : 'text-muted'"
        aria-label="Søg i Cockpit"
        :aria-expanded="mobileSheet === 'search'"
        @click="toggleMobileSheet('search')"
      >
        <span class="mnav-icon" :class="mobileSheet === 'search' ? 'bg-subtle' : ''"><span class="block w-5 h-5" v-html="ICONS.search" /></span>
        <span class="mnav-label">Søg</span>
      </button>
      <button
        v-for="item in visibleNavItems"
        :key="item.path"
        type="button"
        class="mnav-item relative"
        :class="mobileItemActive(item.path) ? 'text-ink' : isEmptyItem(item) ? 'text-faint' : 'text-muted'"
        :aria-label="item.label"
        :aria-expanded="item.subItems ? mobileSheet === item.path : undefined"
        :data-mnav-current="isSectionActive(item.path) ? 'true' : undefined"
        data-testid="mobile-nav-item"
        @click="onMobileNavItem(item)"
      >
        <span class="mnav-icon" :class="mobileItemActive(item.path) ? 'bg-subtle' : ''"><span class="block w-5 h-5" v-html="ICONS[item.icon]" /></span>
        <span class="mnav-label">{{ item.label }}</span>
        <AiDots v-if="aiOn(item.path)" class="absolute top-1.5 left-2" data-testid="ai-activity-nav" />
        <span
          v-if="item.path === '/webshop' && seoReviewOpen + imagesReviewOpen > 0"
          class="absolute top-1.5 right-2 min-w-[16px] h-4 px-1 rounded-full bg-amber-bg text-amber text-[9px] font-semibold flex items-center justify-center"
          aria-hidden="true"
        >{{ seoReviewOpen + imagesReviewOpen > 99 ? '99+' : seoReviewOpen + imagesReviewOpen }}</span>
      </button>
      <button
        v-if="opsGroupVisible"
        type="button"
        class="mnav-item relative"
        :class="mobileItemActive('/ops') ? 'text-ink' : 'text-muted'"
        aria-label="Driftstatus"
        :aria-expanded="mobileSheet === 'ops'"
        :data-mnav-current="isSectionActive('/ops') ? 'true' : undefined"
        @click="toggleMobileSheet('ops')"
      >
        <span class="mnav-icon" :class="mobileItemActive('/ops') ? 'bg-subtle' : ''"><span class="block w-5 h-5" v-html="ICONS.activity" /></span>
        <AiDots v-if="aiOn('/ops')" class="absolute top-1.5 left-2" data-testid="ai-activity-nav" />
        <span class="mnav-label">Driftstatus</span>
      </button>
      <button
        v-if="auth.user"
        type="button"
        class="mnav-item"
        :class="mobileSheet === 'profile' ? 'text-ink' : 'text-muted'"
        aria-label="Profil, tema og log ud"
        :aria-expanded="mobileSheet === 'profile'"
        @click="toggleMobileSheet('profile')"
      >
        <span class="mnav-icon" :class="mobileSheet === 'profile' ? 'bg-subtle' : ''">
          <img v-if="auth.user.avatarUrl" :src="auth.user.avatarUrl" alt="" class="w-6 h-6 rounded-full object-cover ring-1 ring-line" />
          <span v-else class="w-6 h-6 rounded-full bg-subtle text-ink ring-1 ring-line grid place-items-center text-[10px] font-semibold select-none">{{ userInitials }}</span>
        </span>
        <span class="mnav-label">Profil</span>
      </button>
    </div>
  </nav>

  <!-- Arket: glider op nedefra og fylder skærmen OVER baren, så man kan slide
       videre til en anden sektion uden at lukke først. Undermenuen står
       centreret; er den højere end skærmen, ruller arket (my-auto centrerer
       uden at klippe toppen, som justify-center ville). z-[55]: over sidens
       sticky filter-header (50) og drawerne (50), under tooltips (100). -->
  <Transition name="mnav-sheet">
    <div
      v-if="mobileSheet !== null"
      class="md:hidden print:hidden fixed inset-x-0 top-0 bottom-[var(--mobile-nav-h)] z-[55] bg-page flex flex-col"
      role="dialog"
      aria-modal="true"
      :aria-label="mobileSheetTitle"
      data-testid="mobile-sheet"
      @keydown.esc="closeMobileSheet"
    >
      <div class="shrink-0 flex items-center gap-3 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
        <span class="block w-5 h-5 shrink-0 text-muted" v-html="mobileSheetIcon" />
        <p class="flex-1 min-w-0 text-base font-semibold text-ink truncate">{{ mobileSheetTitle }}</p>
        <button
          type="button"
          class="shrink-0 w-9 h-9 grid place-items-center rounded-lg text-muted hover:text-ink hover:bg-hover"
          aria-label="Luk menuen"
          @click="closeMobileSheet"
        >
          <span class="block w-5 h-5" v-html="ICONS.close" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto overscroll-contain">
        <!-- SØGNING: samme søgning som sidebarens, samme resultater. -->
        <div v-if="mobileSheet === 'search'" class="px-4 pb-6" @keydown="onSearchKeydown">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            placeholder="Søg efter side, afsnit eller nøgletal"
            class="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-base text-ink placeholder:text-faint focus:outline-none focus:ring-1 focus:ring-ink/20"
            aria-label="Søg i Cockpit"
            @keydown.enter.prevent="onSearchEnter"
          />
          <p v-if="searchQuery.trim().length < 2" class="mt-3 text-xs text-faint leading-snug">
            Skriv mindst 2 bogstaver. Søgningen finder sider, afsnit, nøgletal og
            opslag i ordbogen, og viser kun det du har adgang til.
          </p>
          <p v-if="searchEmpty" class="mt-3 text-sm text-muted">Ingen træf på "{{ searchQuery.trim() }}".</p>
          <div v-if="searchResults.length > 0" class="mt-2 flex flex-col">
            <button
              v-for="hit in searchResults"
              :key="`${hit.path}|${hit.anchor ?? ''}|${hit.title}`"
              type="button"
              class="text-left px-2 py-2.5 rounded-lg hover:bg-hover"
              @click="goToHit(hit)"
            >
              <span class="block text-sm font-medium text-ink truncate">{{ hit.title }}</span>
              <span class="block text-xs text-faint truncate">{{ kindLabel(hit.kind) }} · {{ hit.context }}</span>
            </button>
          </div>
        </div>

        <div v-else class="min-h-full flex flex-col px-6 py-6">
          <div class="my-auto w-full max-w-xs mx-auto flex flex-col items-stretch text-center">
            <!-- En hovedmenus undermenu i 2 trin (se mobileSheetGroup): først
                 niveau 2, hvor en gruppe med niveau 3 ([D-150]) er en knap med ›;
                 derefter gruppens sider under gruppen som overskrift, med
                 tilbage-pil til oversigten. -->
            <Transition v-if="mobileSheetItem" name="mnav-level" mode="out-in">
              <div
                v-if="mobileSheetGroupItem"
                key="group"
                class="flex flex-col items-stretch"
                data-testid="mobile-sheet-level3"
              >
                <!-- Den valgte gruppe står med SMÅT og en venstre-pil som
                     fortegn (Charlie 25-09-2026): den er vejen tilbage, ikke
                     en overskrift der skal konkurrere med siderne under den. -->
                <button
                  type="button"
                  class="mb-2 self-center flex items-center justify-center gap-1 min-h-[2.25rem] px-3 rounded-lg text-xs font-medium text-muted hover:text-ink hover:bg-hover"
                  :aria-label="`Tilbage til ${mobileSheetItem.label}`"
                  data-testid="mobile-sheet-back"
                  @click="mobileSheetGroup = null"
                >
                  <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                  {{ mobileSheetGroupItem.label }}
                  <AiDots v-if="aiOn(mobileSheetGroupItem.path)" class="ml-1.5" data-testid="ai-activity-nav" />
                  <span
                    v-if="mobileSheetGroupItem.path === '/webshop/seo' && seoReviewOpen > 0"
                    class="inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                  >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                  <span
                    v-if="mobileSheetGroupItem.path === '/webshop/images' && imagesReviewOpen > 0"
                    class="inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                  >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                </button>
                <RouterLink
                  v-for="child in mobileSheetGroupItem.children"
                  :key="child.path"
                  :to="child.path"
                  class="mnav-sheet-link text-muted"
                  data-testid="mobile-sheet-link"
                >
                  {{ child.label }}
                  <AiDots v-if="aiOn(child.path, true)" class="ml-1.5" data-testid="ai-activity-nav" />
                  <span
                    v-if="child.path === '/webshop/seo/review' && seoReviewOpen > 0"
                    class="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                  >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                  <span
                    v-if="child.path === '/webshop/images/review' && imagesReviewOpen > 0"
                    class="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                  >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                </RouterLink>
              </div>
              <div v-else key="overview" class="flex flex-col items-stretch" data-testid="mobile-sheet-level2">
                <template v-for="sub in mobileSheetItem.subItems" :key="sub.path">
                  <button
                    v-if="sub.children"
                    type="button"
                    class="mnav-sheet-link"
                    :class="subIsFaint(mobileSheetItem, sub) ? 'text-faint' : 'text-muted'"
                    :aria-label="`${sub.label}, åbn undermenu`"
                    data-testid="mobile-sheet-group"
                    @click="mobileSheetGroup = sub.path"
                  >
                    {{ sub.label }}
                    <AiDots v-if="aiOn(sub.path)" class="ml-1.5" data-testid="ai-activity-nav" />
                    <span
                      v-if="sub.path === '/webshop/seo' && seoReviewOpen > 0"
                      class="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                    >{{ seoReviewOpen > 99 ? '99+' : seoReviewOpen }}</span>
                    <span
                      v-if="sub.path === '/webshop/images' && imagesReviewOpen > 0"
                      class="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-amber-bg text-amber text-[10px] font-semibold items-center justify-center"
                    >{{ imagesReviewOpen > 99 ? '99+' : imagesReviewOpen }}</span>
                    <span class="ml-1.5 text-faint" aria-hidden="true">›</span>
                  </button>
                  <RouterLink
                    v-else
                    :to="sub.path"
                    class="mnav-sheet-link"
                    :class="subIsFaint(mobileSheetItem, sub) ? 'text-faint' : 'text-muted'"
                    data-testid="mobile-sheet-link"
                  >
                    {{ sub.label }}
                    <AiDots v-if="aiOn(sub.path, true) && !(sub.path === '/doc/agents' && agentsRunning)" class="ml-1.5" data-testid="ai-activity-nav" />
                    <span v-if="sub.path === '/doc/agents' && agentsRunning" class="ml-1 inline-flex items-center gap-0.5 text-sky-600 dark:text-sky-400" aria-label="Agenter kører">
                      <span class="agent-dot" style="animation-delay: 0ms"></span>
                      <span class="agent-dot" style="animation-delay: 150ms"></span>
                      <span class="agent-dot" style="animation-delay: 300ms"></span>
                    </span>
                  </RouterLink>
                </template>
              </div>
            </Transition>

            <!-- Driftstatus-gruppen: samme 3 punkter og samme grants som i bunden
                 af sidebaren. -->
            <template v-else-if="mobileSheet === 'ops'">
              <RouterLink v-if="canSeeOps" to="/ops/jobs" class="mnav-sheet-link text-muted">Kørsler</RouterLink>
              <RouterLink v-if="canSeeSystemLog" to="/ops/log" class="mnav-sheet-link text-muted">Log</RouterLink>
              <RouterLink v-if="canSeeOpsActions" to="/ops/actions" class="mnav-sheet-link text-muted">Handlinger<AiDots v-if="aiOn('/ops/actions', true)" class="ml-1.5" data-testid="ai-activity-nav" /></RouterLink>
              <RouterLink v-if="canSeeOpsDisruptions" to="/ops/disruptions" class="mnav-sheet-link text-muted">Driftsforstyrrelser</RouterLink>
              <button v-if="canSyncData" type="button" class="mnav-sheet-link text-muted" @click="openSyncFromSheet">Manuel sync</button>
            </template>

            <!-- Profilen: det sidebaren har i bunden (tema og log ud) plus Min
                 profil, som ellers ligger under Indstillinger. -->
            <template v-else-if="mobileSheet === 'profile'">
              <RouterLink to="/settings/profile" class="mnav-sheet-link text-muted">Min profil</RouterLink>
              <button type="button" class="mnav-sheet-link text-muted" @click="toggleTheme">{{ themeToggleLabel }}</button>
              <button type="button" class="mnav-sheet-link text-muted" @click="onLogout">Log ud</button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Agenter-menupunktets loading-dots ([D-166]). */
.agent-dot {
  width: 4px;
  height: 4px;
  border-radius: 9999px;
  background: currentColor;
  opacity: 0.25;
  animation: agent-dot-pulse 1.2s ease-in-out infinite;
}
/* Mobilens footer-menu: ét ikon + kort navn pr. punkt, snapper ved slide. */
.mnav-item {
  flex: 0 0 auto;
  width: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  scroll-snap-align: start;
  -webkit-tap-highlight-color: transparent;
}
.mnav-icon {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  transition: background-color 150ms;
}
.mnav-label {
  max-width: 4.25rem;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mnav-sheet-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0 1rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 150ms, color 150ms;
}
/* Arket markerer ALDRIG den side man står på (Charlie 25-09-2026: det
   forvirrer); tonen vises kun mens fingeren trykker, som respons. */
.mnav-sheet-link:active {
  background-color: rgb(var(--hover));
}
/* Skiftet mellem niveau 2 og 3 i arket: kort glid, ind fra højre. */
.mnav-level-enter-active,
.mnav-level-leave-active {
  transition: transform 140ms ease-out, opacity 140ms ease-out;
}
.mnav-level-enter-from {
  transform: translateX(1.5rem);
  opacity: 0;
}
.mnav-level-leave-to {
  transform: translateX(-1.5rem);
  opacity: 0;
}
.mnav-sheet-enter-active,
.mnav-sheet-leave-active {
  transition: transform 220ms ease-out, opacity 220ms ease-out;
}
.mnav-sheet-enter-from,
.mnav-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
@keyframes agent-dot-pulse {
  0%,
  100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
</style>
